import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { EtaxAutoSendRound } from "../../../types/etax-auto-send.type";
import { numberWithCommas } from "../../../utils";
import {
  formatThaiShortDate,
  formatThaiShortDateTimeBkk,
} from "../../../utils/date";
import { headCellSx } from "../documentCells";
import { RoundStatusChip } from "./autoSendCells";
import { ROUND_TRIGGER_LABEL } from "./autoSendMeta";

const BASE_COLUMN_COUNT = 12;

interface RoundsTableProps {
  rows: EtaxAutoSendRound[];
  loading: boolean;
  limit: number;
}

const formatInstant = (value: string | null) =>
  formatThaiShortDateTimeBkk(value, { weekday: false }) || "-";

const CountCell = ({ value }: { value: number }) => (
  <TableCell align="right">{numberWithCommas(value)}</TableCell>
);

export default function RoundsTable({ rows, loading, limit }: RoundsTableProps) {
  // Columns a newer backend adds. Shown only once some row carries them, so an older API
  // keeps the table it had instead of a column of dashes.
  const showInProgress = rows.some((round) => round.in_progress_count != null);
  const showAlreadySent = rows.some(
    (round) => round.skipped_already_sent_count != null,
  );
  const columnCount =
    BASE_COLUMN_COUNT + (showInProgress ? 1 : 0) + (showAlreadySent ? 1 : 0);

  return (
    <TableContainer sx={{ borderRadius: "12px" }}>
      <Table>
        <TableHead sx={{ bgcolor: (theme) => theme.palette.background.default }}>
          <TableRow>
            <TableCell sx={headCellSx}>วันที่รอบ</TableCell>
            <TableCell sx={headCellSx}>ประเภท</TableCell>
            <TableCell sx={headCellSx}>สถานะ</TableCell>
            <TableCell align="right" sx={headCellSx}>
              ครั้งที่
            </TableCell>
            <TableCell align="right" sx={headCellSx}>
              บิลที่เข้ารอบ
            </TableCell>
            <TableCell align="right" sx={headCellSx}>
              ส่งแล้ว
            </TableCell>
            {showInProgress && (
              <TableCell align="right" sx={headCellSx}>
                กำลังส่ง
              </TableCell>
            )}
            <TableCell align="right" sx={headCellSx}>
              ส่งไม่สำเร็จ
            </TableCell>
            <TableCell align="right" sx={headCellSx}>
              ข้าม (ส่งไม่ได้)
            </TableCell>
            <TableCell align="right" sx={headCellSx}>
              ข้าม (ไม่ส่งอัตโนมัติ)
            </TableCell>
            {showAlreadySent && (
              <TableCell align="right" sx={headCellSx}>
                ข้าม (ส่งไปแล้ว)
              </TableCell>
            )}
            <TableCell sx={headCellSx}>เริ่ม</TableCell>
            <TableCell sx={headCellSx}>เสร็จ</TableCell>
            <TableCell>ข้อผิดพลาดล่าสุด</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading &&
            Array.from({ length: limit }).map((_, index) => (
              <TableRow key={index}>
                {Array.from({ length: columnCount }).map((__, colIndex) => (
                  <TableCell key={colIndex}>
                    <Skeleton variant="text" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          {!loading && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columnCount} align="center" sx={{ py: 5 }}>
                <Typography color="text.secondary" fontWeight={500}>
                  ยังไม่มีรอบส่งอัตโนมัติ
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {!loading &&
            rows.map((round, index) => (
              <TableRow
                key={round.round_uuid}
                sx={{
                  "& td, & th": { padding: "8px" },
                  bgcolor: index % 2 === 1 ? "#f6f6f9" : "#fff",
                }}
              >
                <TableCell>
                  <Typography variant="body2" noWrap>
                    {formatThaiShortDate(round.round_date) || round.round_date}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    noWrap
                  >
                    ตัดรอบ {formatInstant(round.scheduled_at)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap>
                    {ROUND_TRIGGER_LABEL[round.trigger] ?? round.trigger}
                  </Typography>
                  {round.triggered_by && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      noWrap
                    >
                      {round.triggered_by}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <RoundStatusChip status={round.status} />
                </TableCell>
                <CountCell value={round.attempt_count} />
                <CountCell value={round.candidate_count} />
                <CountCell value={round.sent_count} />
                {showInProgress && <CountCell value={round.in_progress_count ?? 0} />}
                <CountCell value={round.failed_count} />
                <CountCell value={round.skipped_blocked_count} />
                <CountCell value={round.skipped_held_count} />
                {showAlreadySent && (
                  <CountCell value={round.skipped_already_sent_count ?? 0} />
                )}
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {formatInstant(round.started_at)}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {formatInstant(round.finished_at)}
                </TableCell>
                <TableCell sx={{ maxWidth: 240 }}>
                  {round.last_error ? (
                    <Tooltip
                      arrow
                      title={
                        <Box sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {round.last_error}
                        </Box>
                      }
                    >
                      <Typography variant="body2" color="error" noWrap>
                        {round.last_error}
                      </Typography>
                    </Tooltip>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
